import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { WarningBanner } from '../ui/warning-banner';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Database,
  Package,
  Sparkles,
  ShoppingBag,
  Upload
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Store, storeService } from '../../services/store';
import { SyncProgress } from './SyncProgress';
import { StockUpdateProgress } from './StockUpdateProgress';
import { useOrganization } from '../../contexts/OrganizationContext';
import { SyncDialog } from './SyncDialog';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { useUnifiedSubscription } from '../../contexts/SubscriptionContext';

interface StoreCardProps {
  store: Store;
  onEdit: (store: Store) => void;
  onDelete: (store: Store) => void;
  onUpdate: (store: Store) => void;
}

export function StoreCard({ store, onEdit, onDelete, onUpdate }: StoreCardProps) {
  const { subscription } = useUnifiedSubscription();
  const { hasPermission } = useOrganization();
  const [isSyncing, setIsSyncing] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showSyncProgress, setShowSyncProgress] = useState(false);
  const [isWebhookTriggered, setIsWebhookTriggered] = useState(false);
  const [syncJustCompleted, setSyncJustCompleted] = useState(false);
  const [isDeletingProducts, setIsDeletingProducts] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [isUpdatingStock, setIsUpdatingStock] = useState(false);
  const [stockUpdateResult, setStockUpdateResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showStockUpdateProgress, setShowStockUpdateProgress] = useState(false);
  const [stockUpdateJobId, setStockUpdateJobId] = useState<string | null>(null);


  // Check for active sync on mount
  React.useEffect(() => {
    const savedState = localStorage.getItem(`sync-progress-${store.id}`);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.syncJob && ['pending', 'in_progress'].includes(parsed.syncJob.status)) {
          setShowSyncProgress(true);
        }
      } catch (e) {
        console.error('Failed to parse saved sync state:', e);
      }
    }
  }, [store.id]);

  const getUserFriendlyErrorMessage = (error: string) => {
    // Convert technical errors to user-friendly messages
    if (error.includes('fetch') || error.includes('network') || error.includes('ECONNREFUSED')) {
      return 'Unable to connect to the store. Please check your connection and try again.';
    }
    if (error.includes('timeout')) {
      return 'The store is taking longer than expected to respond. Please try again.';
    }
    if (error.includes('authentication') || error.includes('unauthorized') || error.includes('401')) {
      return 'Authentication failed. Please check your store credentials.';
    }
    if (error.includes('not found') || error.includes('404')) {
      return 'Store not found or may have been removed.';
    }
    if (error.includes('already in progress') || error.includes('sync job')) {
      return 'A sync is already running for this store. Please wait for it to complete.';
    }
    if (error.includes('rate limit') || error.includes('429')) {
      return 'Too many requests. Please wait a moment and try again.';
    }
    // Check for subscription limit errors (sync, credit, store limits)
    if (error.includes('sync(s) per month') || error.includes('Sync Limit Exceeded') || error.includes('plan allows')) {
      return error; // Return the exact subscription error message from the API
    }
    if (error.includes('credit') && (error.includes('limit') || error.includes('insufficient'))) {
      return error; // Return the exact credit limit error message from the API
    }
    if (error.includes('store') && error.includes('limit')) {
      return error; // Return the exact store limit error message from the API
    }
    // Default user-friendly message
    return 'Something went wrong. Please try again or contact support if the issue persists.';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = () => {
    // Show syncing status if sync is in progress
    if (showSyncProgress) {
      return (
        <Badge className="bg-primary/10 text-primary border-primary/20">
          <Database className="w-3 h-3 mr-1" />
          Syncing
        </Badge>
      );
    }

    switch (store.status) {
      case 'connected':
        return (
          <Badge className="bg-success/10 text-success border-success/20">
            <CheckCircle className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        );
      case 'testing':
        return (
          <Badge className="bg-primary/10 text-primary border-primary/20">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Testing
          </Badge>
        );
      case 'syncing':
        return (
          <Badge className="bg-primary/10 text-primary border-primary/20">
            <Database className="w-3 h-3 mr-1" />
            Syncing
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="secondary" className="bg-destructive/10 text-destructive border-destructive/20">
            <XCircle className="w-3 h-3 mr-1" />
            Error
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="bg-muted text-muted-foreground border-border">
            <Clock className="w-3 h-3 mr-1" />
            Disconnected
          </Badge>
        );
    }
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setSyncResult(null);
    setIsWebhookTriggered(false);
    setSyncJustCompleted(false);

    try {
      const result = await storeService.testExistingStoreConnection(store.id);
      setSyncResult(result);
      
      if (result.success) {
        // Update the store status to connected
        const updatedStore = { ...store, status: 'connected' as const, connection_tested_at: new Date().toISOString() };
        onUpdate(updatedStore);
      }
    } catch (error: any) {
      setSyncResult({
        success: false,
        message: getUserFriendlyErrorMessage(error.message || 'Failed to test connection')
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleUpdateStock = async () => {
    setIsUpdatingStock(true);
    setStockUpdateResult(null);

    try {
      const result = await storeService.updateStock(store.id, 'online_wh');

      // Stock update now returns job_id for async processing
      if (result.job_id) {
        setStockUpdateJobId(result.job_id);
        setShowStockUpdateProgress(true);
        setStockUpdateResult({
          success: true,
          message: result.message || 'Stock update started in background'
        });
      } else {
        // Fallback for synchronous response (shouldn't happen with new implementation)
        setStockUpdateResult({
          success: true,
          message: result.message || 'Stock update completed'
        });
      }
    } catch (error: any) {
      setStockUpdateResult({
        success: false,
        message: getUserFriendlyErrorMessage(error.message || 'Failed to update stock data')
      });
    } finally {
      setIsUpdatingStock(false);
    }
  };

  const handleStockUpdateComplete = () => {
    setStockUpdateResult({
      success: true,
      message: 'Stock update completed successfully!'
    });
  };

  const handleCloseStockUpdateProgress = () => {
    setShowStockUpdateProgress(false);
    setStockUpdateJobId(null);
    setStockUpdateResult(null);
  };

  const handleSync = async (storeViewCode: string = 'default', inventorySourceCode: string = 'online_wh') => {
    setIsSyncing(true);
    setSyncResult(null);
    setIsWebhookTriggered(false);
    setSyncJustCompleted(false);
    setShowSyncDialog(false);

    try {
      const result = await storeService.syncStore(store.id, storeViewCode, inventorySourceCode);
      
      if (result.job_id) {
        setIsWebhookTriggered(true);
        setSyncResult({
          success: true,
          message: result.total_products > 0 
            ? `Sync initiated! Processing ${result.total_products.toLocaleString()} products automatically.`
            : 'Sync initiated! Fetching products from your store...'
        });
        
        // Show the sync progress component immediately
        setShowSyncProgress(true);
        
        // Give a short delay to allow the webhook to trigger before starting to poll
        setTimeout(() => {
          setIsSyncing(false);
        }, 2000);
      } else {
        setSyncResult({
          success: false,
          message: getUserFriendlyErrorMessage('Failed to start sync - no job ID returned')
        });
        setIsSyncing(false);
      }
    } catch (error: any) {
      setSyncResult({
        success: false,
        message: getUserFriendlyErrorMessage(error.message || 'Failed to sync store')
      });
      setIsSyncing(false);
    }
  };

  const handleSyncComplete = async () => {
    try {
      // Fetch the updated store data from the backend to get the actual last_sync timestamp
      const updatedStore = await storeService.getStore(store.id);
      onUpdate(updatedStore);
      
      setSyncJustCompleted(true);
      setSyncResult({
        success: true,
        message: 'Product sync completed successfully!'
      });
      setIsWebhookTriggered(false); // Clear webhook state since sync is done
    } catch (error: any) {
      console.error('Failed to refresh store data after sync completion:', error);
      // Fallback to local update if API call fails
      const updatedStore = { 
        ...store, 
        last_sync: new Date().toISOString(),
        status: 'connected' as const
      };
      onUpdate(updatedStore);
      
      setSyncJustCompleted(true);
      setSyncResult({
        success: true,
        message: 'Product sync completed successfully! (Store data refresh failed - please refresh the page to see updated sync time)'
      });
      setIsWebhookTriggered(false);
    }
  };

  const handleCloseSyncProgress = () => {
    setShowSyncProgress(false);
    setSyncResult(null);
    setIsWebhookTriggered(false);
    setSyncJustCompleted(false);
  };

  const handleSyncCancelled = () => {
    // Clean up all sync-related state when sync is cancelled
    setShowSyncProgress(false);
    setSyncResult({
      success: false,
      message: 'Sync job has been cancelled.'
    });
    setIsWebhookTriggered(false);
    setIsSyncing(false);
    setSyncJustCompleted(false);
  };

  const handleDeleteProducts = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteProducts = async () => {
    setShowDeleteConfirm(false);
    setIsDeletingProducts(true);
    setDeleteResult(null);
    setSyncResult(null);

    try {
      const result = await storeService.deleteAllProducts(store.id);
      setDeleteResult({
        success: true,
        message: result.message
      });
      
      // Update the store to reflect no last_sync
      const updatedStore = { ...store, last_sync: undefined };
      onUpdate(updatedStore);
    } catch (error: any) {
      setDeleteResult({
        success: false,
        message: getUserFriendlyErrorMessage(error.message || 'Failed to delete products')
      });
    } finally {
      setIsDeletingProducts(false);
    }
  };


  const isMagento = store.connector_type !== 'upload';

  return (
    <Card className="w-full h-full flex flex-col card-modern-enhanced group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-primary/30 hover:border-l-primary data-[state=open]:shadow-xl data-[state=open]:-translate-y-1 data-[state=open]:border-l-primary">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <CardTitle className="text-xl font-semibold">{store.name}</CardTitle>
              {/* Connector Type Badge */}
              <Badge variant="outline" className={isMagento ? 'border-orange-500/30 text-orange-600 bg-orange-500/10' : 'border-green-500/30 text-green-600 bg-green-500/10'}>
                {isMagento ? (
                  <>
                    <ShoppingBag className="w-3 h-3 mr-1" />
                    Magento
                  </>
                ) : (
                  <>
                    <Upload className="w-3 h-3 mr-1" />
                    Upload
                  </>
                )}
              </Badge>
              {getStatusBadge()}
            </div>
            {/* URL - Only show for Magento */}
            {isMagento && store.url && (
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <ExternalLink className="w-4 h-4" />
                <a
                  href={store.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary hover:underline transition-colors duration-200 truncate max-w-xs"
                >
                  {store.url}
                </a>
              </div>
            )}
            {/* Upload Connector Description */}
            {!isMagento && (
              <div className="text-sm text-muted-foreground">
                Upload images directly for processing and download
              </div>
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="hover:bg-muted transition-colors duration-200 data-[state=open]:bg-muted"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="animate-scale-in">
              {hasPermission('stores', 'edit') && (
                <>
                  <DropdownMenuItem onClick={() => onEdit(store)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Connector
                  </DropdownMenuItem>
                  {/* Magento-specific actions */}
                  {isMagento && (
                    <>
                      <DropdownMenuItem onClick={handleTestConnection} disabled={isTestingConnection}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${isTestingConnection ? 'animate-spin' : ''}`} />
                        {isTestingConnection ? 'Testing...' : 'Test Connection'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleUpdateStock} disabled={isUpdatingStock || !store.last_sync}>
                        <Database className={`w-4 h-4 mr-2 ${isUpdatingStock ? 'animate-spin' : ''}`} />
                        {isUpdatingStock ? 'Updating Stock...' : 'Update Stock Data'}
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}
              {isMagento && store.last_sync && hasPermission('products', 'delete') && (
                <>
                  {hasPermission('stores', 'edit') && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onClick={handleDeleteProducts}
                    disabled={isDeletingProducts || showSyncProgress}
                    className="text-warning focus:text-warning"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Delete All Products
                  </DropdownMenuItem>
                </>
              )}
              {hasPermission('stores', 'delete') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(store)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Connector
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col">
        {/* Main Content */}
        <div className="space-y-6 flex-1">
        {/* Store Details Grid - Magento specific */}
        {isMagento ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Consumer Key</label>
              <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded border truncate">{store.consumer_key}</p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Magento Version</label>
              <p className="text-sm bg-muted/50 px-3 py-2 rounded border">{store.magento_version}</p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last Sync</label>
              <div className="flex items-center text-sm bg-muted/50 px-3 py-2 rounded border">
                {store.last_sync ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                    {formatDate(store.last_sync)}
                  </>
                ) : (
                  <span className="text-muted-foreground">Never synced</span>
                )}
              </div>
            </div>

            {store.last_error && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last Error</label>
                <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded border border-destructive/20 break-words">{store.last_error}</p>
              </div>
            )}
          </div>
        ) : (
          /* Upload Connector Details */
          <div className="p-4 bg-gradient-to-r from-green-500/5 to-emerald-500/10 rounded-lg border border-green-500/20">
            <div className="flex items-center gap-3 text-green-700 dark:text-green-400">
              <Upload className="w-5 h-5" />
              <span className="font-medium">Ready to upload images</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Upload up to 500 images, apply AI transformations, and download the results.
            </p>
          </div>
        )}

        {/* Sync Result Alert */}
        {syncResult && (
          <WarningBanner
            variant={syncResult.success ? "success" : "error"}
            title={syncResult.success ? "Sync Successful" : "Sync Failed"}
            message={`${syncResult.message}${syncResult.success && isWebhookTriggered && showSyncProgress && !syncJustCompleted ? ' Processing in progress...' : ''}`}
            icon={syncResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            className="animate-fade-in"
          />
        )}

        {/* Delete Result Alert */}
        {deleteResult && (
          <WarningBanner
            variant={deleteResult.success ? "success" : "error"}
            title={deleteResult.success ? "Delete Successful" : "Delete Failed"}
            message={deleteResult.message}
            icon={deleteResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            className="animate-fade-in"
          />
        )}

        {/* Stock Update Result Alert */}
        {stockUpdateResult && (
          <WarningBanner
            variant={stockUpdateResult.success ? "success" : "error"}
            title={stockUpdateResult.success ? "Stock Update Successful" : "Stock Update Failed"}
            message={stockUpdateResult.message}
            icon={stockUpdateResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            className="animate-fade-in"
          />
        )}

        {/* Sync Progress Component */}
        {showSyncProgress && (
          <div className="border rounded-lg p-4 bg-primary/5 border-primary/20">
            <SyncProgress
              storeId={store.id}
              onSyncComplete={handleSyncComplete}
              onClose={handleCloseSyncProgress}
              onSyncCancelled={handleSyncCancelled}
            />
          </div>
        )}

        {/* Stock Update Progress Component */}
        {showStockUpdateProgress && stockUpdateJobId && (
          <div className="border rounded-lg p-4 bg-primary/5 border-primary/20">
            <StockUpdateProgress
              jobId={stockUpdateJobId}
              onComplete={handleStockUpdateComplete}
              onClose={handleCloseStockUpdateProgress}
            />
          </div>
        )}

        {/* Trial User Warning */}
        {subscription?.plan_name === 'Trial' && (
          <WarningBanner
            variant="info"
            title="Trial Limitation"
            message="As a trial user, syncing is limited to the first 10 products to help you test the system with your 100 trial credits."
            icon={<Sparkles className="h-4 w-4 text-blue-600" />}
            className="mb-3"
          />
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          {isMagento ? (
            /* Magento Actions */
            <>
              {hasPermission('products', 'sync') && (
                <Button
                  onClick={() => setShowSyncDialog(true)}
                  disabled={isSyncing || store.status !== 'connected' || showSyncProgress}
                  className={`flex-1 ${isWebhookTriggered ? 'bg-success hover:bg-success/90' : ''}`}
                  title={store.status !== 'connected' ? 'Test connection first to enable sync' : 'Sync products from Magento store'}
                >
                  {isSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isWebhookTriggered ? 'Starting...' : 'Creating Job...'}
                    </>
                  ) : showSyncProgress ? (
                    <>
                      <Database className="w-4 h-4 mr-2 animate-pulse" />
                      Sync Active
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync Products
                    </>
                  )}
                </Button>
              )}
            </>
          ) : (
            /* Upload Connector Actions */
            <Button
              onClick={() => window.location.href = `/uploads?connector=${store.id}`}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            >
              <Upload className="w-4 h-4 mr-2" />
              Go to Uploads
            </Button>
          )}

          {hasPermission('stores', 'edit') && (
            <Button
              variant="outline"
              onClick={() => onEdit(store)}
              className="hover:bg-muted"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
        </div>

        {/* Spacer to push footer down */}
        <div className="flex-1" />

        {/* Footer Timestamps */}
        <div className="text-xs text-muted-foreground pt-4 mt-6 border-t border-border flex justify-between">
          <span>Created {formatDate(store.created_at)}</span>
          <span>Updated {formatDate(store.updated_at)}</span>
        </div>
      </CardContent>

      {/* Delete Products Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete All Products"
        description={`Are you sure you want to delete all products from "${store.name}"? This action cannot be undone and will remove all imported products. You'll need to sync again to re-import them.`}
        confirmText="Delete All Products"
        cancelText="Cancel"
        onConfirm={confirmDeleteProducts}
        variant="destructive"
        isLoading={isDeletingProducts}
      />

      {/* Sync Language Selection Dialog */}
      <SyncDialog
        open={showSyncDialog}
        onOpenChange={setShowSyncDialog}
        onConfirm={handleSync}
        storeName={store.name}
        storeId={store.id}
        isLoading={isSyncing}
      />
    </Card>
  );
}